import { useEffect, useMemo, useState } from 'react';
import { groupsApi } from '../../../services/groups-api-client';
import { mutationErrorMessage } from '../../../lib/mutation-error';
import { readDinnerEditError } from '../../../lib/dinnerEdit';
import { normalizeTripDate } from '../../../lib/reminderPlan';
import { resolveTripTimeZone } from '../../../lib/tripTime';
import { buildTripDatesNotice, movesGuestsWouldSee } from '../../../lib/dateShiftNotice';
import type { ChangeNoticeDraft } from '../../../lib/changeNotice';
import {
  EDITED_MEANWHILE,
  dateShiftPayload,
  outsideTripRange,
  planDateShift,
  replanFailed,
  runDateShift,
  type DateShiftFailure,
  type DateShiftMove,
  type DateShiftPlan,
  type DinnerShift,
  type EventShift,
} from '../../../lib/tripDates';
import type { GroupDetail, GroupDinnerHold } from '../../../types/groups';

/** Said whenever moving the schedule needs a real trip zone and there is none: a browser zone must never be pinned onto a dinner. */
export const SET_ZONE_FIRST = "Set the trip's time zone first, then the schedule can be moved.";

/** The most days the "Move by" field accepts either way. */
export const MAX_MOVE_DAYS = 365;

/** What the move dialog is working from. */
export interface TripDatesOffer {
  /** `after_save`: the start date just moved. `catch_up`: rows left outside the dates, brought in later. */
  mode: 'after_save' | 'catch_up';
  /** Whole days each move shifts by (editable in `catch_up`). */
  days: number;
  oldStart: string | null;
  newStart: string;
  /** The trip's last day: with it the catch-up flags rows that would still land outside. */
  newEnd: string;
  items: GroupDetail['itineraryItems'];
  holds: GroupDinnerHold[];
  /** Whether a guest notice may follow (the trip is published). */
  published: boolean;
}

/** Where the dialog stands after at least one attempt that left something behind. */
export interface MoveResult {
  moved: number;
  /** Refused or not saved: "Try the N that didn't move again" re-plans exactly these, from a fresh read. */
  failed: DateShiftFailure[];
  /** Changed by someone else (or gone): left alone on purpose, never retried. */
  changed: DateShiftFailure[];
}

/**
 * What the dates and the moves came to, kept for the guest notice that follows the dialog.
 *
 * It lives in this hook's state, so a notice still owed is lost if the Trip dates card unmounts before the host is done (they
 * navigate to another tab with the dialog open). That is accepted: the dates and the moves are already saved, a notice is only
 * ever an offer, and the host can still write to guests from the Broadcasts tab.
 */
export interface Session {
  before: { start: string | null; end: string | null };
  after: { start: string; end: string };
  days: number | null;
  datesChanged: boolean;
  published: boolean;
  movedEvents: EventShift[];
  movedDinners: DinnerShift[];
}

/** Is the guest notice worth offering for this session? Always after a date change; after a catch-up only if a guest would see a move. */
const worthTellingGuests = (session: Session) => (
  session.published && session.after.start !== '' && session.after.end !== ''
  && (session.datesChanged || movesGuestsWouldSee(session.movedEvents, session.movedDinners))
);

/**
 * The move dialog's session, split out of the Trip dates card's hook: the offer, each attempt, the retry of what did not land,
 * the way back in later, and the one guest notice at the end. Nothing here sends a message.
 *
 *  - `begin`: after the dates are saved, offer to move what hangs off them (from freshly read events and dinners).
 *  - `moveSelected` / `retryFailed`: only what the host ticked; events compared with the server's copy, dinners by version. A
 *    retry re-plans only what did not land, from a fresh read, and moves an item only if it is still at the time the plan
 *    started from.
 *  - `openCatchUp`: the way back in after "Leave them as they are", offering ONLY rows outside the dates.
 */
export function useDateShiftSession(group: GroupDetail, onSaved: () => void) {
  const { timeZone: tripZone, isFallback: zoneMissing } = resolveTripTimeZone(group.timeZone);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [offer, setOffer] = useState<TripDatesOffer | null>(null);
  const [moveError, setMoveError] = useState('');
  const [result, setResult] = useState<MoveResult | null>(null);
  const [summary, setSummary] = useState<MoveResult | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [notice, setNotice] = useState<ChangeNoticeDraft | null>(null);

  const plan: DateShiftPlan | null = useMemo(() => (
    offer ? planDateShift({
      items: offer.items,
      holds: offer.holds,
      days: offer.days,
      tripZone,
      // Only the catch-up judges where a row ends up: after a date change the whole schedule moves together.
      range: offer.mode === 'catch_up' ? { start: offer.newStart, end: offer.newEnd } : undefined,
    }) : null
  ), [offer, tripZone]);

  // The trip's dinners, for the quiet "outside these dates" note (the group itself carries only the itinerary). Read again when the
  // dates or the itinerary change, which is what a move or a save refreshes.
  const [holds, setHolds] = useState<GroupDinnerHold[]>([]);
  // A move that changes only dinners leaves the dates and the itinerary as they were, so this is what makes the note look again.
  const [holdsNonce, setHoldsNonce] = useState(0);
  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => groupsApi.listDinnerHolds(group.id))
      .then((rows) => { if (live) setHolds(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (live) setHolds([]); });
    return () => { live = false; };
  }, [group.id, group.dateStart, group.dateEnd, group.itineraryItems, holdsNonce]);

  // Rows left outside the dates, events and dinners: the card's quiet note.
  const outside = outsideTripRange({ items: group.itineraryItems, holds, dateStart: group.dateStart, dateEnd: group.dateEnd, tripZone });

  const clearMessages = () => { setError(''); setWarning(''); };

  /** The one guest notice for this whole change, offered when the host is done (never sent from here). */
  const offerNotice = (done: Session) => {
    if (!worthTellingGuests(done)) return;
    setNotice(buildTripDatesNotice({
      before: { start: done.before.start ?? undefined, end: done.before.end ?? undefined },
      after: done.after,
      days: done.days,
      datesChanged: done.datesChanged,
      events: done.movedEvents,
      dinners: done.movedDinners,
    }));
  };

  /** The dialog is finished with (the host is done): remember what happened, then offer the notice. */
  const finish = (final: MoveResult | null) => {
    setHoldsNonce((n) => n + 1);
    setOffer(null);
    setResult(null);
    setMoveError('');
    setSummary(final && (final.moved > 0 || final.failed.length > 0 || final.changed.length > 0) ? final : null);
    if (session) offerNotice(session);
    setSession(null);
  };

  /** After the dates are saved: offer to move the schedule too. Returns true when the dialog opened. */
  const begin = async (fresh: GroupDetail, next: Session, oldStart: string, newStart: string, days: number): Promise<boolean> => {
    if (zoneMissing) {
      // Moving a time needs the trip's own zone: without one, the browser's would be pinned onto the dinners. Offer nothing, say why.
      const holdsExist = await groupsApi.listDinnerHolds(group.id).then((rows) => rows.some((hold) => hold.reservedAt), () => false);
      if (holdsExist || (fresh.itineraryItems || []).some((item) => item.startsAt || item.endsAt)) {
        setWarning(`The dates are saved. ${SET_ZONE_FIRST} Until then the itinerary and dinners stay on their current dates.`);
      }
      return false;
    }
    let holds: GroupDinnerHold[] = [];
    try {
      holds = await groupsApi.listDinnerHolds(group.id);
    } catch (e) {
      setWarning(`The dates are saved, but the dinners could not be checked, so they were not offered. ${mutationErrorMessage(e)}`.trim());
    }
    const planned = planDateShift({ items: fresh.itineraryItems, holds, days, tripZone });
    if (planned.events.length + planned.dinners.length === 0) return false;
    setSession(next);
    setOffer({ mode: 'after_save', days, oldStart, newStart, newEnd: next.after.end, items: fresh.itineraryItems, holds, published: next.published });
    return true;
  };

  /** Brings rows that sit outside the trip's dates in line with them, later, from a fresh read. Rows already inside are not offered. */
  const openCatchUp = async () => {
    clearMessages();
    setSummary(null);
    if (zoneMissing) { setWarning(SET_ZONE_FIRST); return; }
    setBusy(true);
    try {
      let fresh: GroupDetail;
      try {
        fresh = await groupsApi.getGroup(group.id);
      } catch (e) {
        setError(`Could not re-check the itinerary, so nothing was changed. ${mutationErrorMessage(e)}`.trim());
        return;
      }
      let holds: GroupDinnerHold[] = [];
      try {
        holds = await groupsApi.listDinnerHolds(group.id);
      } catch (e) {
        setWarning(`The dinners could not be checked, so only the itinerary is offered. ${mutationErrorMessage(e)}`.trim());
      }
      const found = outsideTripRange({ items: fresh.itineraryItems, holds, dateStart: fresh.dateStart, dateEnd: fresh.dateEnd, tripZone });
      if (found.count === 0) {
        setWarning('Nothing sits outside the trip dates any more.');
        return;
      }
      const start = normalizeTripDate(fresh.dateStart) ?? '';
      const end = normalizeTripDate(fresh.dateEnd) ?? start;
      const published = Boolean(fresh.published);
      setSession({
        before: { start: null, end: null }, after: { start, end }, days: found.suggestedDays, datesChanged: false, published, movedEvents: [], movedDinners: [],
      });
      setResult(null);
      setMoveError('');
      setOffer({ mode: 'catch_up', days: found.suggestedDays, oldStart: null, newStart: start, newEnd: end, items: found.items, holds: found.holds, published });
    } finally {
      setBusy(false);
    }
  };

  /** Changes the shift in `catch_up` (the suggestion assumes the schedule began on the old start date). Bounded to a year either way. */
  const setOfferDays = (days: number) => {
    if (!offer || offer.mode !== 'catch_up' || !Number.isInteger(days) || Math.abs(days) > MAX_MOVE_DAYS) return;
    setOffer({ ...offer, days });
    setSession((prev) => (prev ? { ...prev, days } : prev));
  };

  const writers = {
    saveEvent: (move: EventShift) => groupsApi.upsertItineraryItem(group.id, dateShiftPayload(move)),
    saveDinner: (move: DinnerShift) => groupsApi.updateDinnerHold(group.id, move.hold.id, move.patch.body),
    describeEventError: mutationErrorMessage,
    // The dinner editor tells a host to "reload"; here a retry is on offer, so a stale version is said plainly instead.
    describeDinnerError: (e: unknown) => {
      const read = readDinnerEditError(e);
      return read.kind === 'stale' ? EDITED_MEANWHILE : read.message;
    },
  };

  /** Folds one attempt into the running totals and decides whether the dialog is done or stays open for the rest. */
  const settle = (attempt: {
    movedEvents: EventShift[];
    movedDinners: DinnerShift[];
    failed: DateShiftFailure[];
    changed: DateShiftFailure[];
    movedBefore: number;
  }) => {
    const nextSession = session && {
      ...session,
      movedEvents: [...session.movedEvents, ...attempt.movedEvents],
      movedDinners: [...session.movedDinners, ...attempt.movedDinners],
    };
    if (nextSession) setSession(nextSession);
    const total = attempt.movedBefore + attempt.movedEvents.length + attempt.movedDinners.length;
    const next: MoveResult = { moved: total, failed: attempt.failed, changed: attempt.changed };
    onSaved();
    setHoldsNonce((n) => n + 1);
    if (next.failed.length === 0 && next.changed.length === 0) {
      setOffer(null);
      setResult(null);
      setSummary(next);
      if (nextSession) offerNotice(nextSession);
      setSession(null);
    } else {
      setResult(next);   // the dialog stays open: it says what landed and offers the rest again
    }
  };

  /** The host's ticks: events first, each compared with the server's copy; then dinners, by version. */
  const moveSelected = async (selected: DateShiftMove[]) => {
    if (!offer) return;
    const events = selected.filter((move): move is EventShift => move.kind === 'event');
    const dinners = selected.filter((move): move is DinnerShift => move.kind === 'dinner');
    setBusy(true);
    setMoveError('');
    try {
      let freshItems: GroupDetail['itineraryItems'] = [];
      if (events.length > 0) {
        try {
          freshItems = (await groupsApi.getGroup(group.id)).itineraryItems;
        } catch (e) {
          // The dialog stays open with the ticks as they were: pressing Move again is the way forward.
          setMoveError(`Could not re-check the itinerary before moving it, so nothing was moved. ${mutationErrorMessage(e)}`.trim());
          return;
        }
      }
      const outcome = await runDateShift({ events, dinners, freshItems, ...writers });
      settle({ ...outcome, changed: [], movedBefore: 0 });
    } finally {
      setBusy(false);
    }
  };

  /** "Try the N that didn't move again": only those, from a fresh read, and only if each is still where the plan found it. */
  const retryFailed = async () => {
    if (!offer || !result || result.failed.length === 0) return;
    const failedMoves = result.failed.map((failure) => failure.move);
    setBusy(true);
    setMoveError('');
    try {
      let items: GroupDetail['itineraryItems'];
      try {
        items = (await groupsApi.getGroup(group.id)).itineraryItems;
      } catch (e) {
        setMoveError(`Could not re-check the itinerary, so nothing was moved. Press the button again to retry. ${mutationErrorMessage(e)}`.trim());
        return;
      }
      let holds: GroupDinnerHold[] = [];
      if (failedMoves.some((move) => move.kind === 'dinner')) {
        try {
          holds = await groupsApi.listDinnerHolds(group.id);
        } catch (e) {
          setMoveError(`Could not re-check the dinners, so nothing was moved. Press the button again to retry. ${mutationErrorMessage(e)}`.trim());
          return;
        }
      }
      const replanned = replanFailed({ failed: failedMoves, items, holds, days: offer.days, tripZone });
      const outcome = await runDateShift({ events: replanned.events, dinners: replanned.dinners, freshItems: items, ...writers });
      const landed = replanned.alreadyMoved;
      settle({
        movedEvents: [...outcome.movedEvents, ...landed.filter((move): move is EventShift => move.kind === 'event')],
        movedDinners: [...outcome.movedDinners, ...landed.filter((move): move is DinnerShift => move.kind === 'dinner')],
        failed: outcome.failed,
        changed: [...result.changed, ...replanned.changed],
        movedBefore: result.moved,
      });
    } finally {
      setBusy(false);
    }
  };

  return {
    busy, error, warning, setWarning, clearMessages, offer, plan, moveError, result, summary, clearSummary: () => setSummary(null),
    notice, setNotice, outside, zoneMissing, begin, offerNotice, openCatchUp, setOfferDays, moveSelected, retryFailed, finish,
  };
}
